// Option C — the 28 domains read as a vaidya reads a case.
// Flat and numbered is how the data is stored; agni / nidra / manas / mala /
// srotas is how it is interpreted. Each group expands to its own domains, with
// the answered count so a blank is never mistaken for a low score.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { Section, Score, Meter, Delta, Label, Empty } from "@/components/clinical/kit";
import { groupSummary } from "@/config/clinical";
import { interpret } from "@/lib/status";

function GroupCard({ g, changeByWid, open, onToggle }) {
  const { t } = useTranslation();
  const it = interpret(g.avg);
  return (
    <div className={`rounded-lg border transition ${open ? "border-emerald-300" : "border-[#E5DFD3]"}`}
      data-testid={`group-${g.key}`}>
      <button type="button" onClick={onToggle}
        className="flex w-full items-center gap-3 rounded-lg px-4 py-3 text-left hover:bg-[#F6F8F5]"
        data-testid={`group-toggle-${g.key}`}>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-base font-bold text-stone-900">{t(g.label)}</div>
          <div className="truncate text-xs text-stone-500">{t(g.sub)}</div>
          <div className="mt-1.5"><Meter value={g.avg} color={it.color} /></div>
        </div>
        <div className="flex-shrink-0 text-right">
          <Score value={g.avg} color={it.color} />
          <div className="font-mono text-[10px] uppercase tracking-wider text-stone-400">
            {g.scoredCount}/{g.domains.length} {t("scored")}
          </div>
        </div>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-stone-400 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-[#EFE9DE] px-4 py-2">
          {g.domains.map((d) => {
            const di = interpret(d.score);
            const ch = changeByWid?.[d.wid];
            return (
              <div key={d.wid} className="flex items-center gap-3 border-b border-[#F5F1E9] py-1.5 last:border-0"
                data-testid={`group-domain-${d.wid}`}>
                <span className="font-mono text-[11px] text-stone-400">{d.num}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-stone-700">{t(d.label)}</span>
                {d.score == null && d.answered > 0 && (
                  <span className="rounded-full bg-sky-50 px-2 py-0.5 font-mono text-[10px] text-sky-700">
                    {d.answered}/8
                  </span>
                )}
                <Delta value={ch?.step} />
                <span className="w-8 text-right"><Score value={d.score} color={di.color} size="sm" /></span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function DomainGroups({ scores, responses, changes }) {
  const { t } = useTranslation();
  const groups = groupSummary(scores, responses);
  const changeByWid = Object.fromEntries((changes || []).map((c) => [c.wid, c]));
  const scoredAny = groups.some((g) => g.avg != null);
  // Open the groups that need attention; leave the healthy ones folded away.
  const [open, setOpen] = useState(() =>
    Object.fromEntries(groups.map((g) => [g.key, g.avg != null && g.avg < 5])));

  const weakest = groups.filter((g) => g.avg != null).sort((a, b) => a.avg - b.avg).slice(0, 3);

  return (
    <Section title="Where the imbalance sits" sub="28 scored domains grouped the way a case is read"
      testid="domain-groups"
      right={
        <button type="button" data-testid="groups-expand-all"
          onClick={() => {
            const allOpen = groups.every((g) => open[g.key]);
            setOpen(Object.fromEntries(groups.map((g) => [g.key, !allOpen])));
          }}
          className="rounded-md border border-[#E5DFD3] px-3 py-1 text-xs text-stone-600 hover:bg-stone-50">
          {groups.every((g) => open[g.key]) ? t("Collapse all") : t("Expand all")}
        </button>
      }>
      {!scoredAny ? (
        <Empty>No domain has all eight statements answered yet, so nothing can be scored.</Empty>
      ) : (
        <>
          {weakest.length > 0 && (
            <p className="mb-3 text-sm text-stone-600">
              <Label>Weakest groups</Label>{" "}
              {weakest.map((g, i) => (
                <span key={g.key}>
                  {i > 0 && ", "}
                  <b className="text-stone-800">{t(g.label)}</b>{" "}
                  <span style={{ color: interpret(g.avg).color }}>{g.avg}</span>
                </span>
              ))}
            </p>
          )}
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            {groups.map((g) => (
              <GroupCard key={g.key} g={g} changeByWid={changeByWid} open={!!open[g.key]}
                onToggle={() => setOpen((o) => ({ ...o, [g.key]: !o[g.key] }))} />
            ))}
          </div>
        </>
      )}
    </Section>
  );
}
